import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, CheckCircle2, Circle } from "lucide-react";
import RestTimer from "./RestTimer";
import { haptic } from "../lib/haptics";
import { notifyBadgeUnlock } from "../lib/badges";
import { primeChime } from "../lib/chime";

const mkRow = (i) => ({
  id: `s-${i}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  weight_kg: "",
  reps: "",
  done: false,
});

/**
 * Set entry list for a non-hold exercise. Owns its own sets[] + rest timer
 * state so the parent ExerciseCard stays presentational. Logs to
 * POST /api/workouts/log-set and calls `onLogged` after success so the
 * parent can refresh history/progress charts.
 *
 * Props:
 *   - exerciseName: string
 *   - muscleGroup: string
 *   - targetSets: number — prescribed set count
 *   - restSec: number — between-set rest window
 *   - isBodyweight: bool — hide the weight input + × separator
 *   - dayIndex, exIndex: number — used to namespace data-testid attrs
 *   - onLogged: fn — fired after a successful POST so parent can refetch history
 */
export default function SetList({
  exerciseName,
  muscleGroup,
  targetSets,
  restSec,
  isBodyweight,
  dayIndex,
  exIndex,
  onLogged,
}) {
  const [sets, setSets] = useState(() =>
    Array.from({ length: Math.max(1, targetSets) }, (_, i) => mkRow(i))
  );
  const [saving, setSaving] = useState(false);
  const [restRemaining, setRestRemaining] = useState(null);
  const restIntervalRef = useRef(null);

  // Tick down the rest timer
  useEffect(() => {
    if (restRemaining === null) return;
    if (restRemaining <= 0) return; // RestTimer handles completion side-effects
    restIntervalRef.current = setTimeout(
      () => setRestRemaining((r) => (r === null ? null : r - 1)),
      1000
    );
    return () => clearTimeout(restIntervalRef.current);
  }, [restRemaining]);

  const updateSet = (i, key, val) => {
    setSets((s) => s.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));
  };

  const addSet = () => setSets((s) => [...s, mkRow(s.length)]);

  const removeSet = (i) => setSets((s) => s.filter((_, idx) => idx !== i));

  const toggleDone = (i) => {
    const justDone = !sets[i].done;
    primeChime();
    setSets((s) =>
      s.map((row, idx) => (idx === i ? { ...row, done: !row.done } : row))
    );
    if (justDone) setRestRemaining(restSec);
  };

  const skipRest = () => {
    clearTimeout(restIntervalRef.current);
    setRestRemaining(null);
  };

  const saveSets = async () => {
    const filled = sets
      .filter((s) => s.weight_kg !== "" || s.reps !== "")
      .map((s) => ({
        weight_kg: Number(s.weight_kg) || 0,
        reps: Number(s.reps) || 0,
      }));
    if (filled.length === 0) return toast.error("Enter at least one set");
    setSaving(true);
    try {
      const { data } = await api.post("/workouts/log-set", {
        exercise_name: exerciseName,
        muscle_group: muscleGroup,
        sets: filled,
      });
      haptic.medium();
      toast.success("Sets logged");
      notifyBadgeUnlock(data?.badge_unlocked);
      onLogged?.();
    } catch (err) {
      console.error("Failed to log sets:", err);
      toast.error("Failed to log");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="label-eyebrow text-white/50 mb-2">
        {isBodyweight ? "Log this week's reps" : "Log this week's sets"}
      </div>
      <div className="space-y-2">
        {sets.map((s, i) => (
          <div
            key={s.id}
            className={`flex items-center gap-2 transition-opacity ${s.done ? "opacity-60" : ""}`}
          >
            <button
              type="button"
              onClick={() => toggleDone(i)}
              className={`shrink-0 transition-colors ${
                s.done ? "text-[#6B9BFF]" : "text-white/30 hover:text-white/60"
              }`}
              data-testid={`set-done-${dayIndex}-${exIndex}-${i}`}
              aria-label={s.done ? "Mark set incomplete" : "Mark set complete"}
            >
              {s.done ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
            </button>
            <span className="text-[10px] uppercase tracking-widest text-white/40 w-12">
              Set {i + 1}
            </span>
            {!isBodyweight && (
              <>
                <input
                  type="number"
                  step="0.5"
                  value={s.weight_kg}
                  onChange={(e) => updateSet(i, "weight_kg", e.target.value)}
                  placeholder="kg"
                  className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-[#6B9BFF]"
                  data-testid={`set-weight-${dayIndex}-${exIndex}-${i}`}
                />
                <span className="text-white/30 text-xs">×</span>
              </>
            )}
            <input
              type="number"
              value={s.reps}
              onChange={(e) => updateSet(i, "reps", e.target.value)}
              placeholder="reps"
              className={`${isBodyweight ? "flex-1" : "w-20"} bg-[#1A1A1A] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-[#6B9BFF]`}
              data-testid={`set-reps-${dayIndex}-${exIndex}-${i}`}
            />
            <button
              type="button"
              onClick={() => removeSet(i)}
              className="text-white/20 hover:text-[#FF3B30] p-1"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      <RestTimer
        remaining={restRemaining}
        restSec={restSec}
        onSkip={skipRest}
        onComplete={() => setRestRemaining(null)}
        testid={`rest-timer-${dayIndex}-${exIndex}`}
      />

      <div className="flex items-center gap-2 mt-3">
        <button
          onClick={addSet}
          className="text-xs text-white/50 hover:text-white inline-flex items-center gap-1"
          data-testid={`add-set-${dayIndex}-${exIndex}`}
        >
          <Plus className="w-3 h-3" /> Add set
        </button>
        <div className="flex-1" />
        <button
          onClick={saveSets}
          disabled={saving}
          className="bg-[#6B9BFF] text-black font-bold uppercase tracking-wider text-xs px-4 py-2 hover:bg-white disabled:opacity-50 inline-flex items-center gap-2"
          data-testid={`save-sets-${dayIndex}-${exIndex}`}
        >
          <Save className="w-3.5 h-3.5" /> Log sets
        </button>
      </div>
    </div>
  );
}
